import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Sparkles, Loader2, Plus, Trash2, Save, Printer, Truck, Users, Box, Mail, Copy, CheckCircle2, Globe } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { PageHeader, Field } from "../components/Shared";
import { api, eur, fmtDate, formatApiError } from "../lib/api";
import { FORMULAS, QUOTE_STATUS } from "../lib/labels";

const EMPTY = {
  client_id: "", client_name: "", client_email: "", client_phone: "", origin_address: "", destination_address: "",
  distance_km: 0, move_date: "", origin_floor: 0, dest_floor: 0, origin_elevator: false, dest_elevator: false, items: [],
  formula: "standard", option_packing: false, option_lift: false, option_insurance: false, insured_value: 0, discount_pct: 0,
  status: "brouillon", notes: "",
};
const NUMERIC = ["distance_km", "origin_floor", "dest_floor", "insured_value", "discount_pct"];

function AiEstimator({ onResult }) {
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [notes, setNotes] = useState("");
  const run = async () => {
    setLoading(true);
    try {
      const { data } = await api.post("/ai/estimate", { inventory_text: text });
      onResult(data.items);
      setNotes(data.notes);
      toast.success(`${data.items.length} objets identifiés · ${data.volume_m3} m³`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  return (
    <div className="rounded-xl border border-teal-200 bg-gradient-to-br from-teal-50 to-blue-50 p-5" data-testid="ai-estimator">
      <div className="mb-3 flex items-center gap-2"><Sparkles className="h-4 w-4 text-teal-600" /><h3 className="text-base font-semibold text-slate-900">Estimateur IA du volume</h3></div>
      <Textarea data-testid="ai-inventory-input" rows={4} className="bg-white" value={text} onChange={(e) => setText(e.target.value)}
        placeholder="Ex : T4, 3 chambres avec lits doubles, 1 canapé d'angle, frigo américain, lave-linge, 30 cartons, piano droit, 2 vélos…" />
      <Button data-testid="ai-estimate-button" disabled={loading || text.trim().length < 3} onClick={run} className="mt-3 bg-[#0b2c6b] hover:bg-[#1559d6]">
        {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}{loading ? "Analyse en cours…" : "Analyser avec l'IA"}
      </Button>
      {notes && <p className="mt-3 rounded-lg bg-white/70 p-3 text-xs text-slate-600" data-testid="ai-estimate-notes">{notes}</p>}
    </div>
  );
}

function ItemsTable({ items, setItems }) {
  const update = (i, k, v) => setItems(items.map((it, j) => (j === i ? { ...it, [k]: k === "name" ? v : Number(v) } : it)));
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5" data-testid="quote-items">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-base font-semibold text-slate-900">Inventaire ({items.length})</h3>
        <Button size="sm" variant="outline" data-testid="add-item-button" onClick={() => setItems([...items, { name: "", quantity: 1, unit_volume_m3: 0.5 }])}><Plus className="mr-1 h-4 w-4" />Ligne</Button>
      </div>
      <div className="space-y-2">
        <div className="grid grid-cols-12 gap-2 text-xs font-semibold text-slate-500"><span className="col-span-6">Objet</span><span className="col-span-2">Qté</span><span className="col-span-3">m³ unit.</span></div>
        {items.map((it, i) => (
          <div key={i} className="grid grid-cols-12 items-center gap-2" data-testid={`item-row-${i}`}>
            <Input className="col-span-6 h-9" value={it.name} onChange={(e) => update(i, "name", e.target.value)} data-testid={`item-name-${i}`} />
            <Input className="col-span-2 h-9" type="number" min="0" value={it.quantity} onChange={(e) => update(i, "quantity", e.target.value)} data-testid={`item-qty-${i}`} />
            <Input className="col-span-3 h-9" type="number" min="0" step="0.05" value={it.unit_volume_m3} onChange={(e) => update(i, "unit_volume_m3", e.target.value)} data-testid={`item-vol-${i}`} />
            <Button size="icon" variant="ghost" className="col-span-1" onClick={() => setItems(items.filter((_, j) => j !== i))} data-testid={`item-delete-${i}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
          </div>
        ))}
        {items.length === 0 && <p className="py-4 text-center text-sm text-slate-400">Utilisez l'estimateur IA ou ajoutez des lignes manuellement.</p>}
      </div>
    </div>
  );
}

function Summary({ preview }) {
  if (!preview) return null;
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-5 lg:sticky lg:top-6" data-testid="quote-summary">
      <div className="grid grid-cols-3 gap-2">
        {[[Box, `${preview.volume_m3} m³`, "Volume", "summary-volume"], [Truck, preview.truck, "Véhicule", "summary-truck"], [Users, preview.movers, "Déménageurs", "summary-movers"]].map(([Icon, v, l, t]) => (
          <div key={l} className="rounded-lg bg-slate-50 p-3">
            <Icon className="h-4 w-4 text-teal-500" />
            <p className="mt-1 text-sm font-bold leading-tight text-[#0b2c6b]" data-testid={t}>{v}</p>
            <p className="text-[11px] text-slate-500">{l}</p>
          </div>
        ))}
      </div>
      <div className="mt-5 space-y-2 text-sm">
        {preview.lines.map((l) => (
          <div key={l.label} className="flex justify-between gap-3"><span className="text-slate-600">{l.label}</span><span className="font-mono-num">{eur(l.amount)}</span></div>
        ))}
        <div className="flex justify-between border-t pt-2"><span>Total HT</span><span className="font-mono-num" data-testid="summary-ht">{eur(preview.subtotal_ht)}</span></div>
        <div className="flex justify-between"><span>TVA 20 %</span><span className="font-mono-num">{eur(preview.tva)}</span></div>
        <div className="flex justify-between rounded-lg bg-[#0b2c6b] p-3 text-white"><span className="font-semibold">Total TTC</span><span className="font-mono-num text-lg font-bold" data-testid="summary-ttc">{eur(preview.total_ttc)}</span></div>
        <p className="text-xs text-slate-500">Acompte 30 % à la réservation : {eur(preview.total_ttc * 0.3)}</p>
      </div>
    </div>
  );
}

export default function QuoteEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(EMPTY);
  const [clients, setClients] = useState([]);
  const [preview, setPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  const sendToClient = async () => {
    if (!form.client_email) {
      toast.error("Renseignez l'e-mail du client avant l'envoi");
      return;
    }
    setSending(true);
    try {
      await api.put(`/quotes/${id}`, payload(form));
      const { data } = await api.post(`/quotes/${id}/send`);
      setForm((f) => ({ ...f, status: data.status, sent_at: data.sent_at, sent_to: data.sent_to, public_token: data.public_token }));
      toast.success(`Devis envoyé à ${data.sent_to}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSending(false);
    }
  };
  const publicUrl = form.public_token ? `${window.location.origin}/devis-client/${form.public_token}` : "";

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data));
    if (id) api.get(`/quotes/${id}`).then((r) => setForm({ ...EMPTY, ...r.data }));
  }, [id]);

  useEffect(() => {
    const t = setTimeout(() => {
      api.post("/quotes/preview", payload(form)).then((r) => setPreview(r.data)).catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [form]);

  const set = (k) => (e) => {
    const v = e?.target ? e.target.value : e;
    setForm((f) => ({ ...f, [k]: NUMERIC.includes(k) ? Number(v) : v }));
  };
  const pickClient = (cid) => {
    const c = clients.find((x) => x.id === cid);
    if (c) setForm((f) => ({ ...f, client_id: c.id, client_name: c.name, client_email: c.email, client_phone: c.phone, origin_address: f.origin_address || [c.address, c.city].filter(Boolean).join(", ") }));
  };
  const [country, setCountry] = useState(null);
  const seq = useRef(0);
  const detectCountry = async () => {
    const q = (form.origin_address || "").trim();
    if (id || q.length < 5) return;
    const n = ++seq.current;
    const { data } = await api.get("/geo/country", { params: { q } }).catch(() => ({ data: null }));
    if (n === seq.current) setCountry(data?.country_code && !data.francophone ? data : null);
  };
  const switchClientLang = async () => {
    const c = clients.find((x) => x.id === form.client_id);
    try {
      const { data } = await api.put(`/clients/${c.id}`, { ...c, lang: "en" });
      setClients((list) => list.map((x) => (x.id === c.id ? { ...x, ...data, lang: "en" } : x)));
      toast.success(`${c.name} recevra désormais ses e-mails et documents en anglais`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };
  const selectedClient = clients.find((x) => x.id === form.client_id);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = id ? await api.put(`/quotes/${id}`, payload(form)) : await api.post("/quotes", payload(form));
      toast.success(`Devis ${data.number} enregistré`);
      if (!id) navigate(`/app/devis/${data.id}`, { replace: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fade-up">
      <PageHeader overline={form.number || "Nouveau devis"} title={id ? `Devis ${form.number || ""}` : "Nouveau devis"} subtitle="Inventaire, options et prix calculé automatiquement.">
        {id && <Button variant="outline" data-testid="quote-print-button" onClick={() => navigate(`/app/devis/${id}/imprimer`)}><Printer className="mr-2 h-4 w-4" />PDF</Button>}
        {id && (
          <Button variant="outline" data-testid="quote-send-button" disabled={sending} onClick={sendToClient} className="border-teal-300 text-teal-700 hover:bg-teal-50">
            {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Mail className="mr-2 h-4 w-4" />}{form.sent_at ? "Renvoyer au client" : "Envoyer au client"}
          </Button>
        )}
        <Button data-testid="quote-save-button" disabled={saving} onClick={save} className="bg-[#0b2c6b] hover:bg-[#1559d6]"><Save className="mr-2 h-4 w-4" />Enregistrer</Button>
      </PageHeader>
      {!id && <p className="-mt-4 mb-6 text-xs text-slate-500">Enregistrez le devis pour pouvoir l'envoyer par e-mail au client.</p>}
      {form.sent_at && (
        <div className="-mt-2 mb-6 flex flex-col gap-2 rounded-xl border border-teal-200 bg-teal-50/60 p-4 text-sm sm:flex-row sm:items-center sm:justify-between" data-testid="quote-sent-banner">
          <p className="text-teal-800">
            <CheckCircle2 className="mr-1 inline h-4 w-4" />Envoyé à <b>{form.sent_to}</b> le {fmtDate(form.sent_at)}
            {form.decision_by_name && <> · {form.status === "accepte" ? "Accepté" : "Refusé"} en ligne par <b>{form.decision_by_name}</b></>}
          </p>
          <Button size="sm" variant="ghost" data-testid="quote-copy-link-button" onClick={() => { navigator.clipboard?.writeText(publicUrl); toast.success("Lien de validation copié"); }}><Copy className="mr-2 h-4 w-4" />Copier le lien client</Button>
        </div>
      )}
      {country && selectedClient?.lang !== "en" && (
        <div className="mb-6 flex flex-col gap-2 rounded-xl border border-blue-200 bg-blue-50/70 p-4 text-sm sm:flex-row sm:items-center sm:justify-between" data-testid="quote-lang-suggestion">
          <p className="text-[#0b2c6b]"><Globe className="mr-1 inline h-4 w-4" />Adresse de départ en {country.country} : envoyer les e-mails et documents en anglais ?
            {!selectedClient && <span className="block text-xs text-slate-500">Sélectionnez d'abord le client existant pour lui appliquer l'anglais.</span>}</p>
          {selectedClient && <Button size="sm" data-testid="quote-lang-switch-button" onClick={switchClientLang} className="bg-[#1559d6] hover:bg-[#0b2c6b]">Passer {selectedClient.name} en anglais</Button>}
        </div>
      )}
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="space-y-6 lg:col-span-2">
          <div className="grid gap-3 rounded-xl border border-slate-200 bg-white p-5 sm:grid-cols-2" data-testid="quote-client-section">
            <Field label="Client existant">
              <Select value={form.client_id || undefined} onValueChange={pickClient}>
                <SelectTrigger data-testid="quote-client-select"><SelectValue placeholder="Choisir un client" /></SelectTrigger>
                <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Nom du client"><Input data-testid="quote-client-name-input" value={form.client_name} onChange={set("client_name")} /></Field>
            <Field label="E-mail client"><Input data-testid="quote-client-email-input" value={form.client_email} onChange={set("client_email")} /></Field>
            <Field label="Date du déménagement"><Input data-testid="quote-date-input" type="date" value={form.move_date} onChange={set("move_date")} /></Field>
            <Field label="Adresse de départ"><Input data-testid="quote-origin-input" value={form.origin_address} onChange={set("origin_address")} onBlur={detectCountry} /></Field>
            <Field label="Adresse d'arrivée"><Input data-testid="quote-destination-input" value={form.destination_address} onChange={set("destination_address")} /></Field>
            <Field label="Distance (km)"><Input data-testid="quote-distance-input" type="number" min="0" value={form.distance_km} onChange={set("distance_km")} /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Étage départ"><Input data-testid="quote-origin-floor-input" type="number" min="0" value={form.origin_floor} onChange={set("origin_floor")} /></Field>
              <Field label="Étage arrivée"><Input data-testid="quote-dest-floor-input" type="number" min="0" value={form.dest_floor} onChange={set("dest_floor")} /></Field>
            </div>
            <label className="flex items-center gap-2 text-sm"><Switch data-testid="quote-origin-elevator" checked={form.origin_elevator} onCheckedChange={set("origin_elevator")} />Ascenseur au départ</label>
            <label className="flex items-center gap-2 text-sm"><Switch data-testid="quote-dest-elevator" checked={form.dest_elevator} onCheckedChange={set("dest_elevator")} />Ascenseur à l'arrivée</label>
          </div>
          <AiEstimator onResult={(items) => setForm((f) => ({ ...f, items }))} />
          <ItemsTable items={form.items} setItems={(items) => setForm((f) => ({ ...f, items }))} />
          <div className="grid gap-4 rounded-xl border border-slate-200 bg-white p-5 sm:grid-cols-2" data-testid="quote-options">
            <Field label="Formule">
              <Select value={form.formula} onValueChange={set("formula")}>
                <SelectTrigger data-testid="quote-formula-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(FORMULAS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Statut du devis">
              <Select value={form.status} onValueChange={set("status")}>
                <SelectTrigger data-testid="quote-status-select"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(QUOTE_STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <label className="flex items-center gap-2 text-sm"><Switch data-testid="option-packing" checked={form.option_packing} onCheckedChange={set("option_packing")} />Emballage & cartons</label>
            <label className="flex items-center gap-2 text-sm"><Switch data-testid="option-lift" checked={form.option_lift} onCheckedChange={set("option_lift")} />Monte-meuble</label>
            <label className="flex items-center gap-2 text-sm"><Switch data-testid="option-insurance" checked={form.option_insurance} onCheckedChange={set("option_insurance")} />Assurance ad valorem</label>
            {form.option_insurance && <Field label="Valeur déclarée (€)"><Input data-testid="quote-insured-value-input" type="number" min="0" value={form.insured_value} onChange={set("insured_value")} /></Field>}
            <Field label="Remise (%)"><Input data-testid="quote-discount-input" type="number" min="0" max="50" value={form.discount_pct} onChange={set("discount_pct")} /></Field>
            <div className="sm:col-span-2"><Field label="Notes (visibles sur le devis)"><Textarea data-testid="quote-notes-input" rows={2} value={form.notes} onChange={set("notes")} /></Field></div>
          </div>
        </div>
        <div><Summary preview={preview} /></div>
      </div>
    </div>
  );
}

function payload(f) {
  const keys = Object.keys(EMPTY);
  return Object.fromEntries(keys.map((k) => [k, f[k]]));
}
